import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getProjectStats } from "./ProjectsService";
import { useApiCall } from "../useApiCall";
import "./ProjectsPage.css";

const PRIORITY_ORDER = ["CRITICAL", "HIGH", "MEDIUM", "LOW", "SOMEDAY"];
const PRIORITY_COLOR = { CRITICAL: "#ef4444", HIGH: "#f97316", MEDIUM: "#eab308", LOW: "#22c55e", SOMEDAY: "#94a3b8" };

export default function ProjectsPage() {
  const nav = useNavigate();
  const { execute, loading } = useApiCall();
  const [projects, setProjects] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState("ALL"); // ALL | ACTIVE | DONE

  useEffect(() => {
    execute(getProjectStats).then(d => { if (d) setProjects(d); });
  }, []); // eslint-disable-line

  const toggleExpand = (name) => setExpanded(p => p === name ? null : name);

  const filtered = projects.filter(p => {
    if (filter === "ACTIVE") return p.done < p.total;
    if (filter === "DONE")   return p.done === p.total && p.total > 0;
    return true;
  });

  return (
    <div className="proj-page">
      <div className="proj-header">
        <h2>Projects</h2>
        <div className="proj-filter-row">
          {["ALL", "ACTIVE", "DONE"].map(f => (
            <button key={f} className={`proj-filter-btn ${filter === f ? "active" : ""}`} onClick={() => setFilter(f)}>
              {f.charAt(0) + f.slice(1).toLowerCase()}
            </button>
          ))}
          <button className="proj-new-btn" onClick={() => nav("/planner/new")}>+ New item</button>
        </div>
      </div>

      {loading && <div className="proj-loading">Loading projects…</div>}
      {!loading && filtered.length === 0 && (
        <div className="proj-empty">No projects yet. Tag planner items with a project name to see them here.</div>
      )}

      <div className="proj-grid">
        {filtered.map(p => {
          const pct = p.total > 0 ? Math.round((p.done / p.total) * 100) : 0;
          const isOpen = expanded === p.name;
          const activeItems = p.items.filter(i => i.status !== "DONE" && i.status !== "SKIPPED" && i.status !== "CANCELLED");
          const priorityCounts = activeItems.reduce((acc, i) => {
            acc[i.priority] = (acc[i.priority] || 0) + 1; return acc;
          }, {});

          return (
            <div key={p.name} className={`proj-card ${p.overdue > 0 ? "proj-card-overdue" : ""}`}>
              <div className="proj-card-top" onClick={() => toggleExpand(p.name)}>
                <div className="proj-card-name">{p.name}</div>
                <div className="proj-card-meta">
                  {p.overdue > 0 && <span className="proj-overdue-badge">{p.overdue} overdue</span>}
                  {p.nextDue && <span className="proj-next-due">next: {p.nextDue}</span>}
                </div>
              </div>

              {/* Progress bar */}
              <div className="proj-progress-wrap">
                <div className="proj-progress-bar">
                  <div className="proj-progress-fill" style={{ width: pct + "%" }} />
                </div>
                <span className="proj-progress-label">{p.done}/{p.total} done · {pct}%</span>
              </div>

              {/* Priority pills */}
              {Object.keys(priorityCounts).length > 0 && (
                <div className="proj-priority-row">
                  {PRIORITY_ORDER.filter(pri => priorityCounts[pri]).map(pri => (
                    <span key={pri} className="proj-priority-pill"
                          style={{ background: PRIORITY_COLOR[pri] + "22", color: PRIORITY_COLOR[pri] }}>
                      {priorityCounts[pri]} {pri.toLowerCase()}
                    </span>
                  ))}
                </div>
              )}

              {/* Actions */}
              <div className="proj-card-actions">
                <button className="proj-action-btn" onClick={() => nav(`/planner?project=${encodeURIComponent(p.name)}`)}>
                  View in Planner
                </button>
                <button className="proj-action-btn" onClick={() => toggleExpand(p.name)}>
                  {isOpen ? "Hide" : "Items ▾"}
                </button>
              </div>

              {/* Expandable item list */}
              {isOpen && (
                <div className="proj-item-list">
                  {p.items.slice(0, 15).map(i => (
                    <div key={i.id} className={`proj-item ${i.status === "DONE" ? "proj-item-done" : ""}`}>
                      <span className="proj-item-title">{i.title}</span>
                      <span className="proj-item-status">{i.status}</span>
                    </div>
                  ))}
                  {p.items.length > 15 && (
                    <div className="proj-item-more">{p.items.length - 15} more…</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
